import { Button } from '@/components/Button';
import { AdPreviewResponse } from '@/models/ad/types';
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import { MediaPreview } from '@/pages/campaign-details/MediaPreview';
import { StatsBlock } from '@/components/StatsBlock';
import { FC } from 'react';
import dayjs from 'dayjs';
import { EnhancedTableHead } from '@/pages/campaign-details/EnhancedTableHead';
import { useNavigate } from 'react-router';
import { Trans } from 'react-i18next';

export const AdsTable: FC<{
  data?: AdPreviewResponse[];
  isLoading?: boolean;
}> = ({ data = [], isLoading }) => {
  const navigate = useNavigate();

  const handleClick = (id: string) => {
    navigate(`/client/details/ad/${id}`);
  };

  return (
    <>
      <TableContainer component={Paper}>
        <Table>
          <EnhancedTableHead />

          <TableBody>
            {isLoading ? <Placeholder /> : null}

            {!isLoading &&
              data?.map((ad: AdPreviewResponse) => {
                return (
                  <TableRow
                    key={ad.id}
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                  >
                    <TableCell>{ad.title}</TableCell>
                    <TableCell>
                      <MediaPreview mediaUrl={ad.url} />
                    </TableCell>
                    <TableCell>
                      <StatsBlock
                        current={ad.stats.displays?.current}
                        total={ad.stats.displays?.total}
                      />
                    </TableCell>
                    <TableCell>
                      <StatsBlock
                        current={ad.stats.views?.current}
                        total={ad.stats.views?.total}
                      />
                    </TableCell>
                    <TableCell>{ad.reviewStatus}</TableCell>
                    <TableCell>
                      {ad.publishedAt
                        ? dayjs(ad.publishedAt).format('DD.MM.YYYY, HH:mm:ss')
                        : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2 flex-col">
                        <Button
                          variant="contained"
                          color="primary"
                          size="medium"
                          onClick={() => handleClick(ad.id)}
                        >
                          <Trans i18nKey="campaign_details.ads_table.open" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>
    </>
  );
};

const Placeholder = () => (
  <>
    {Array.from({ length: 7 }, (_, index) => (
      <TableRow
        key={`loading-row-${index}`}
        hover
        role="checkbox"
        tabIndex={-1}
        sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
      >
        <TableCell>
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-300 rounded"></div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-24 bg-gray-300 rounded w-full"></div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="h-4 bg-gray-300 rounded w-3/4"></div>
            </div>
          </div>
        </TableCell>
        <TableCell>
          <div className="animate-pulse flex space-x-4">
            <div className="flex-1 space-y-4 py-1">
              <div className="space-y-2">
                <div className="h-4 bg-gray-300 rounded"></div>
                <div className="h-4 bg-gray-300 rounded w-5/6"></div>
              </div>
            </div>
          </div>
        </TableCell>
      </TableRow>
    ))}
  </>
);
